import {
  AlertCircle,
  FileText,
  Info,
  LoaderCircle,
  Trash2,
  UploadCloud,
  X,
} from "lucide-react";
import React from "react";
import { useNavigate } from "react-router-dom";
import { getApiErrorMessage, portalApi } from "../api/client";
import type { ApiEnvelope } from "../api/types";
import {
  Button,
  DatePicker,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Textarea,
} from "../components/ui";
import {
  DOCUMENT_UPLOAD_EXTENSIONS,
  DOCUMENT_UPLOAD_MAX_BYTES,
  fileToBase64,
  formatBytes,
} from "../lib/files";
import type { AssetRequirementSummary } from "../types/compliance";

interface BulkUploadModalProps {
  open: boolean;
  cdassettype: number;
  nmassettype: string;
  requirement: AssetRequirementSummary;
  onClose(): void;
}

export function BulkUploadModal({
  open,
  cdassettype,
  nmassettype,
  requirement,
  onClose,
}: BulkUploadModalProps) {
  const navigate = useNavigate();
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [files, setFiles] = React.useState<File[]>([]);
  const [validity, setValidity] = React.useState("");
  const [comment, setComment] = React.useState("");
  const [dragging, setDragging] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [batchId, setBatchId] = React.useState<number | null>(null);
  const [uploaded, setUploaded] = React.useState(0);
  const [error, setError] = React.useState("");

  React.useEffect(() => {
    if (!open) return;
    setFiles([]);
    setValidity("");
    setComment("");
    setBatchId(null);
    setUploaded(0);
    setError("");
  }, [open, requirement.cdtyperequeriment]);

  function addFiles(selected: File[]) {
    setError("");
    const valid: File[] = [];
    for (const file of selected) {
      const extension = `.${file.name.split(".").pop()?.toLowerCase()}`;
      if (!DOCUMENT_UPLOAD_EXTENSIONS.includes(extension)) {
        setError("Use arquivos PDF, DOC, DOCX, JPG ou PNG.");
      } else if (file.size > DOCUMENT_UPLOAD_MAX_BYTES) {
        setError(`O arquivo ${file.name} excede o limite de 10 MB.`);
      } else {
        valid.push(file);
      }
    }
    setFiles((current) =>
      [...current, ...valid].filter(
        (file, index, all) =>
          all.findIndex(
            (candidate) =>
              candidate.name === file.name &&
              candidate.size === file.size &&
              candidate.lastModified === file.lastModified
          ) === index
      )
    );
  }

  async function continueToReview() {
    if (files.length === 0) {
      setError("Selecione ao menos um arquivo.");
      return;
    }
    if (requirement.fgvalidcontrol === 1 && !validity) {
      setError("Informe a validade da remessa.");
      return;
    }

    setSubmitting(true);
    setError("");
    let currentBatch = batchId;
    let completed = 0;
    try {
      if (!currentBatch) {
        const response = await portalApi.post<ApiEnvelope<{ cdbatch: number }>>(
          "/portal/requirements/batches",
          {
            cdtyperequeriment: requirement.cdtyperequeriment,
            cdsubjecttype: cdassettype,
            ...(validity ? { dtvalidity: validity } : {}),
            ...(comment.trim() ? { txcomment: comment.trim() } : {}),
          }
        );
        currentBatch = response.data.data.cdbatch;
        setBatchId(currentBatch);
      }

      for (const file of files) {
        await portalApi.post(
          `/portal/requirements/batches/${currentBatch}/files`,
          {
            filename: file.name,
            contentBase64: await fileToBase64(file),
          }
        );
        completed += 1;
        setUploaded((value) => value + 1);
      }
      navigate(`/assets/${cdassettype}/batches/${currentBatch}`);
    } catch (requestError) {
      if (completed > 0) {
        setFiles((current) => current.slice(completed));
      }
      setError(
        completed > 0
          ? `${completed} arquivo(s) foram adicionados ao lote. ${getApiErrorMessage(
              requestError,
              "O envio foi interrompido."
            )}`
          : getApiErrorMessage(
              requestError,
              "Não foi possível preparar o lote."
            )
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen && !submitting) onClose();
      }}
    >
      <DialogContent
        showCloseButton={false}
        className="max-h-[92vh] max-w-2xl gap-0 overflow-y-auto p-0"
      >
        <DialogHeader className="flex-row items-start gap-4 border-b border-slate-200 p-5 text-left">
          <div className="min-w-0 flex-1 space-y-1">
            <p className="text-xs text-muted-foreground">{nmassettype}</p>
            <DialogTitle>
              Envio em massa · {requirement.sgrequirement}
            </DialogTitle>
            <DialogDescription>
              {requirement.nmrequirement}
            </DialogDescription>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label="Fechar"
            disabled={submitting}
            onClick={onClose}
          >
            <X size={19} />
          </Button>
        </DialogHeader>

        <div className="space-y-5 p-5">
          <div className="flex items-start gap-3 rounded-xl border border-blue-200 bg-blue-50 p-4 text-sm text-blue-800">
            <Info className="mt-0.5 shrink-0" size={18} />
            <p>
              Inclua o código do ativo ou seu documento no nome de cada arquivo.
              Você poderá corrigir vínculos antes de confirmar.
            </p>
          </div>

          <input
            ref={inputRef}
            type="file"
            multiple
            accept={DOCUMENT_UPLOAD_EXTENSIONS.join(",")}
            className="sr-only"
            onChange={(event) => {
              addFiles(Array.from(event.target.files ?? []));
              event.target.value = "";
            }}
          />
          <Button
            type="button"
            variant="outline"
            className={`h-auto w-full flex-col rounded-xl border-dashed p-7 text-center ${
              dragging
                ? "border-teal-600 bg-teal-50"
                : "border-slate-300 bg-slate-50 hover:border-teal-500"
            }`}
            disabled={submitting}
            onClick={() => inputRef.current?.click()}
            onDragEnter={(event) => {
              event.preventDefault();
              setDragging(true);
            }}
            onDragOver={(event) => event.preventDefault()}
            onDragLeave={(event) => {
              event.preventDefault();
              setDragging(false);
            }}
            onDrop={(event) => {
              event.preventDefault();
              setDragging(false);
              addFiles(Array.from(event.dataTransfer.files));
            }}
          >
            <UploadCloud className="text-teal-700" size={27} />
            <span className="mt-2 text-sm font-medium text-slate-800">
              Arraste os arquivos ou clique para selecionar
            </span>
            <span className="mt-1 text-xs font-normal text-slate-500">
              PDF, DOC, DOCX, JPG ou PNG · até 10 MB por arquivo
            </span>
          </Button>

          {files.length > 0 && (
            <div className="divide-y divide-slate-200 overflow-hidden rounded-xl border border-slate-200">
              {files.map((file) => (
                <div
                  key={`${file.name}-${file.size}-${file.lastModified}`}
                  className="flex items-center gap-3 p-3"
                >
                  <FileText className="shrink-0 text-slate-500" size={18} />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm text-slate-800">
                      {file.name}
                    </p>
                    <p className="text-xs text-slate-500">
                      {formatBytes(file.size)}
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    disabled={submitting}
                    aria-label={`Remover ${file.name}`}
                    onClick={() =>
                      setFiles((current) =>
                        current.filter((candidate) => candidate !== file)
                      )
                    }
                  >
                    <Trash2 size={17} />
                  </Button>
                </div>
              ))}
            </div>
          )}

          {requirement.fgvalidcontrol === 1 && (
            <div>
              <Label htmlFor="bulk-validity">
                Validade para todos <span className="text-red-600">*</span>
              </Label>
              <DatePicker
                id="bulk-validity"
                className="mt-1.5 bg-white"
                value={validity}
                disabled={submitting || batchId !== null}
                onValueChange={setValidity}
                required
                placeholder="Selecione a validade"
              />
            </div>
          )}

          <div>
            <Label htmlFor="bulk-comment">
              Comentário da remessa{" "}
              <span className="font-normal text-muted-foreground">
                (opcional)
              </span>
            </Label>
            <Textarea
              id="bulk-comment"
              className="mt-1.5 min-h-20 resize-y bg-white"
              maxLength={1000}
              value={comment}
              disabled={submitting || batchId !== null}
              onChange={(event) => setComment(event.target.value)}
            />
          </div>

          {error && (
            <div className="portal-message portal-message-error" role="alert">
              <AlertCircle size={18} />
              <p>{error}</p>
            </div>
          )}
        </div>

        <DialogFooter className="flex-row flex-wrap items-center justify-between gap-3 border-t border-slate-200 bg-slate-50 p-4 sm:justify-between">
          <p className="text-xs text-slate-500">
            {uploaded > 0
              ? `${uploaded} arquivo(s) já adicionados ao lote`
              : `${files.length} arquivo(s) selecionados`}
          </p>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              disabled={submitting}
              onClick={onClose}
            >
              Cancelar
            </Button>
            <Button
              type="button"
              disabled={
                submitting ||
                files.length === 0 ||
                (requirement.fgvalidcontrol === 1 && !validity)
              }
              onClick={() => void continueToReview()}
            >
              {submitting && (
                <LoaderCircle className="animate-spin" size={17} />
              )}
              {submitting ? "Preparando…" : "Conferir vínculos"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
